import ModalWrapper from '../../components/modals/ModalWrapper';
import { ReactElement, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../../store/types';
import { AdyenCheckout, Core } from '@adyen/adyen-web/auto';
import { PaymentMethodsResponse } from '@adyen/adyen-web';
import { ENVIRONMENTS } from '../../../../services/exports/Constants';
import PaymentManager from '../../../../services/api/PaymentManager';
import Content from './Content';
import { IModal } from '../../router/ModalRouter';
import { IonSkeletonText } from '@ionic/react';
import ModalHeader from '../../components/modals/ModalHeader';
import { ImperativeAddCard } from './Content';
import useEffectOnce from '../../common/hooks/useEffectOnce';
import { useTranslation } from 'react-i18next';
import WindowResource from '../../../../services/resources/WindowResource';
import { Modals } from '../../exports/Enums';
import toast from 'react-hot-toast';
import paymentLog from '../../../../services/helpers/PaymentLogger';

const AddAdyenCardModal = (props: Partial<IModal>): ReactElement => {
  const { t } = useTranslation(null, {
    keyPrefix:
      'Modules:Mobile:Features:Profile:Components:ProfileModal:NestedModals:PaymentMethods:AddAdyenCard:AddAdyenCardModal',
  });
  const { i18n } = useTranslation();
  const { goBack } = props;
  const ref = useRef<ImperativeAddCard>(null);
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const [paymentMethodsResponse, setPaymentMethodsResponse] = useState<PaymentMethodsResponse | null>(null);
  const [saving, setSaving] = useState(false);

  useEffectOnce(() => {
    loadPaymentMethodsConfig();
  });

  async function loadPaymentMethodsConfig() {
    const { success, response, status } = await PaymentManager.getAdyenPaymentMethodsConfig({
      company_id: company?.id,
      shopper_locale: i18n.language,
      browser_info: {
        acceptHeader: navigator.userAgent,
        colorDepth: screen.colorDepth,
        javaEnabled: false,
        javaScriptEnabled: true,
        language: navigator.language,
        screenHeight: screen.height,
        screenWidth: screen.width,
        timeZoneOffset: new Date().getTimezoneOffset(),
        userAgent: navigator.userAgent,
      },
    });

    if (success) {
      setPaymentMethodsResponse(response.data);

      return;
    }

    paymentLog.error('adyen', 'add_card_payment_methods_load_failed', {
      company_id: company?.id,
      status,
      response,
    });
  }

  const onTokenSuccess = () => {
    setSaving(false);
    toast.success(t('feedback.success'));
    goBack();
  };

  const adyenPromise = useMemo<Promise<Core> | null>(() => {
    if (!company?.id || !company?.service_provider?.adyen_key || !paymentMethodsResponse) return null;

    return AdyenCheckout({
      paymentMethodsResponse,
      clientKey: company.service_provider.adyen_key,
      environment: import.meta.env.VITE_MODE === ENVIRONMENTS.PROD ? 'live' : 'test',
      showPayButton: false,
      locale: i18n.language,
      countryCode: company.country,
      amount: {
        value: 0,
        currency: company.currency,
      },
      onSubmit: async (state, component, actions) => {
        paymentLog.info('adyen', 'add_card_submit', {
          company_id: company.id,
          method_type: state?.data?.paymentMethod?.type,
          method_brand: (state?.data?.paymentMethod as any)?.brand,
        });

        const result = await PaymentManager.createAdyenToken({
          company_id: company.id,
          adyen_payment_data: state.data,
          return_url: `${WindowResource.getOriginWithPathname()}?open_modal=${Modals.AddAdyenCard}&mobile_app=true`,
        });

        if (result.status === 402) {
          const paymentIntent = result.response?.response?.data?.payment_intent;

          if (paymentIntent?.action) {
            paymentLog.info('adyen', 'add_card_3ds_required', {
              company_id: company.id,
              action_type: paymentIntent.action?.type,
            });
            actions.resolve(paymentIntent);

            return;
          }
        }

        if (result.success) {
          actions.resolve({ resultCode: 'Authorised' });
          onTokenSuccess();
        } else {
          paymentLog.error('adyen', 'add_card_create_token_failed', {
            company_id: company.id,
            status: result.status,
            message: result.response?.message,
          });
          setSaving(false);
          actions.reject();
        }
      },
      onAdditionalDetails: async (state) => {
        paymentLog.info('adyen', 'add_card_additional_details', {
          company_id: company.id,
        });

        const { success, status, response } = await PaymentManager.submitAdyenTokenDetails({
          company_id: company.id,
          adyen_payment_data: state.data,
        });

        if (success) {
          onTokenSuccess();
        } else {
          paymentLog.error('adyen', 'add_card_additional_details_failed', {
            company_id: company.id,
            status,
            response,
          });
          setSaving(false);
        }
      },
      onError: (error) => {
        paymentLog.error('adyen', 'add_card_sdk_error', {
          company_id: company.id,
          name: (error as any)?.name,
          message: (error as any)?.message,
          error_code: (error as any)?.errorCode,
        });
        setSaving(false);
      },
    });
  }, [company?.service_provider?.adyen_key, company?.id, company?.currency, paymentMethodsResponse]);

  const addCard = async (): Promise<void> => {
    setSaving(true);
    ref.current?.addCard();
  };

  return (
    <ModalWrapper className="mt-4 h-[90dvh]">
      <ModalHeader
        title={t('title')}
        onBack={() => goBack()}
        endText={t('buttons.save')}
        loading={saving}
        onEndClick={addCard}
      />
      {adyenPromise ? (
        <Content ref={ref} adyenPromise={adyenPromise} {...props} />
      ) : (
        <div className="flex flex-col gap-4 p-sm">
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
        </div>
      )}
    </ModalWrapper>
  );
};

export default AddAdyenCardModal;
